export type WaTemplateCategory = 'Booking' | 'Payment' | 'Reminder' | 'Visa' | 'Custom';
export type WaMessageStatus = 'Sent' | 'Delivered' | 'Read' | 'Failed' | 'Pending';

export interface WaVariable {
  key: string;
  label: string;
  example: string;
}

export interface WaTemplate {
  id: string;
  name: string;
  category: WaTemplateCategory;
  icon: string;
  color: string;
  description: string;
  usageCount: number;
  lastUsed: string;
  status: 'Active' | 'Pending Approval' | 'Rejected';
  variables: WaVariable[];
  bodyText: string;
  headerText?: string;
  footerText?: string;
  buttons?: { type: 'URL' | 'PHONE' | 'QUICK_REPLY'; label: string; value: string }[];
}

export interface WaSentMessage {
  id: string;
  recipientName: string;
  recipientPhone: string;
  recipientAvatar: string;
  templateId: string;
  templateName: string;
  category: WaTemplateCategory;
  bookingRef?: string;
  destination?: string;
  sentAt: string;
  deliveredAt?: string;
  readAt?: string;
  status: WaMessageStatus;
  agent: string;
  agentAvatar: string;
  previewText: string;
}

export const waTemplates: WaTemplate[] = [
  {
    id: 'tpl-1',
    name: 'Booking Confirmation',
    category: 'Booking',
    icon: 'ri-calendar-check-line',
    color: '#10b981',
    description: 'Send immediately after booking is confirmed. Includes full trip details.',
    usageCount: 187,
    lastUsed: '2026-03-26',
    status: 'Active',
    headerText: 'Your Booking is Confirmed!',
    bodyText: 'Dear {{customer_name}},\n\nGreat news! Your booking *{{booking_ref}}* has been confirmed.\n\n*Destination:* {{destination}}\n*Travel Date:* {{travel_date}}\n*Passengers:* {{pax}} person(s)\n*Total Amount:* AED {{total_amount}}\n\nFor any queries, reach out to your agent {{agent_name}}.\n\nWe look forward to making your journey unforgettable!',
    footerText: 'Horizon Travel & Tourism — Dubai',
    buttons: [
      { type: 'URL', label: 'View Booking', value: 'https://horizontravel.ae/booking/{{booking_ref}}' },
      { type: 'PHONE', label: 'Call Us', value: '+97142001234' },
    ],
    variables: [
      { key: 'customer_name', label: 'Customer Name', example: 'Ahmed Al-Rashid' },
      { key: 'booking_ref', label: 'Booking Reference', example: 'BK-1084' },
      { key: 'destination', label: 'Destination', example: 'Maldives' },
      { key: 'travel_date', label: 'Travel Date', example: 'April 15, 2026' },
      { key: 'pax', label: 'Passengers', example: '2' },
      { key: 'total_amount', label: 'Total Amount', example: '15,000' },
      { key: 'agent_name', label: 'Agent Name', example: 'Sara Ahmed' },
    ],
  },
  {
    id: 'tpl-2',
    name: 'Payment Reminder',
    category: 'Payment',
    icon: 'ri-money-dollar-circle-line',
    color: '#f59e0b',
    description: 'Send when payment balance is due. Reminds clients of outstanding amounts.',
    usageCount: 134,
    lastUsed: '2026-03-25',
    status: 'Active',
    headerText: 'Payment Reminder',
    bodyText: 'Dear {{customer_name}},\n\nThis is a friendly reminder that your balance payment of *AED {{balance_amount}}* for booking *{{booking_ref}}* ({{destination}}) is due on *{{due_date}}*.\n\nPayment Methods:\n\u2022 Bank Transfer to Account: 1234567890\n\u2022 Credit Card (link below)\n\nPlease make the payment before the due date to avoid cancellation.\n\nThank you!',
    footerText: 'Horizon Travel & Tourism',
    buttons: [
      { type: 'URL', label: 'Pay Online', value: 'https://horizontravel.ae/pay/{{booking_ref}}' },
      { type: 'QUICK_REPLY', label: 'I\'ve Paid', value: 'PAID_CONFIRM' },
    ],
    variables: [
      { key: 'customer_name', label: 'Customer Name', example: 'Emma Williams' },
      { key: 'balance_amount', label: 'Balance Amount', example: '17,500' },
      { key: 'booking_ref', label: 'Booking Reference', example: 'BK-1083' },
      { key: 'destination', label: 'Destination', example: 'Mediterranean' },
      { key: 'due_date', label: 'Due Date', example: 'April 22, 2026' },
    ],
  },
  {
    id: 'tpl-3',
    name: 'Payment Received',
    category: 'Payment',
    icon: 'ri-checkbox-circle-line',
    color: '#10b981',
    description: 'Confirm receipt of payment. Sent automatically after recording a payment.',
    usageCount: 98,
    lastUsed: '2026-03-24',
    status: 'Active',
    headerText: '\u2705 Payment Received',
    bodyText: 'Dear {{customer_name}},\n\nWe\'ve received your payment of *AED {{paid_amount}}* for booking *{{booking_ref}}*.\n\n*Payment Method:* {{payment_method}}\n*Transaction Ref:* {{transaction_ref}}\n*Date:* {{payment_date}}\n\n*Balance Remaining:* AED {{balance}}\n\nThank you for your payment. Your trip to {{destination}} is all set!',
    footerText: 'Horizon Travel & Tourism',
    buttons: [
      { type: 'URL', label: 'Download Receipt', value: 'https://horizontravel.ae/receipt/{{transaction_ref}}' },
    ],
    variables: [
      { key: 'customer_name', label: 'Customer Name', example: 'Omar Hassan' },
      { key: 'paid_amount', label: 'Paid Amount', example: '6,300' },
      { key: 'booking_ref', label: 'Booking Reference', example: 'BK-1082' },
      { key: 'payment_method', label: 'Payment Method', example: 'Bank Transfer' },
      { key: 'transaction_ref', label: 'Transaction Ref', example: 'TRF-20032026-005' },
      { key: 'payment_date', label: 'Payment Date', example: 'March 20, 2026' },
      { key: 'balance', label: 'Balance', example: '0' },
      { key: 'destination', label: 'Destination', example: 'Egypt' },
    ],
  },
  {
    id: 'tpl-4',
    name: 'Pre-Departure Reminder',
    category: 'Reminder',
    icon: 'ri-flight-takeoff-line',
    color: '#6366f1',
    description: 'Send 48–72 hours before departure. Includes checklist and key info.',
    usageCount: 76,
    lastUsed: '2026-03-23',
    status: 'Active',
    headerText: '\u2708\ufe0f Departure in {{days_until}} Days!',
    bodyText: 'Dear {{customer_name}},\n\nYour trip to *{{destination}}* is just around the corner!\n\n*Departure:* {{travel_date}}\n*Booking Ref:* {{booking_ref}}\n\n*Pre-Departure Checklist:*\n\u2705 Valid passport (6+ months)\n\u2705 Visa / ETA confirmed\n\u2705 Travel insurance\n\u2705 Foreign currency\n\u2705 Hotel vouchers downloaded\n\n*Your Emergency Contact:*\n{{agent_name}}: {{agent_phone}}\n\nHave a wonderful trip!',
    footerText: 'Horizon Travel & Tourism',
    buttons: [
      { type: 'URL', label: 'View Itinerary', value: 'https://horizontravel.ae/itinerary/{{booking_ref}}' },
      { type: 'PHONE', label: 'Call Agent', value: '{{agent_phone}}' },
    ],
    variables: [
      { key: 'customer_name', label: 'Customer Name', example: 'Priya Nair' },
      { key: 'destination', label: 'Destination', example: 'Thailand & Bali' },
      { key: 'travel_date', label: 'Travel Date', example: 'May 10, 2026' },
      { key: 'booking_ref', label: 'Booking Reference', example: 'BK-1081' },
      { key: 'days_until', label: 'Days Until Departure', example: '3' },
      { key: 'agent_name', label: 'Agent Name', example: 'Fatima Al-Zahra' },
      { key: 'agent_phone', label: 'Agent Phone', example: '+971 50 555 1234' },
    ],
  },
  {
    id: 'tpl-5',
    name: 'Visa Ready Notification',
    category: 'Visa',
    icon: 'ri-passport-line',
    color: '#8b5cf6',
    description: 'Notify customers when their visa has been approved and is ready.',
    usageCount: 52,
    lastUsed: '2026-03-21',
    status: 'Active',
    headerText: '\ud83c\udf89 Your Visa is Approved!',
    bodyText: 'Dear {{customer_name}},\n\nExcellent news! Your *{{visa_type}}* visa has been *approved*!\n\n*Visa Number:* {{visa_number}}\n*Valid From:* {{valid_from}}\n*Valid Until:* {{valid_until}}\n*Entries:* {{entries}}\n\nPlease collect your original passport and visa sticker from our office at your earliest convenience.\n\n*Office Hours:* Sun–Thu, 9AM–6PM\n*Address:* Business Bay, Dubai',
    footerText: 'Horizon Travel & Tourism',
    buttons: [
      { type: 'QUICK_REPLY', label: 'Confirm Collection', value: 'VISA_COLLECT' },
      { type: 'PHONE', label: 'Call Office', value: '+97142001234' },
    ],
    variables: [
      { key: 'customer_name', label: 'Customer Name', example: 'Maria Santos' },
      { key: 'visa_type', label: 'Visa Type', example: 'Schengen' },
      { key: 'visa_number', label: 'Visa Number', example: 'SCH-2026-44210' },
      { key: 'valid_from', label: 'Valid From', example: 'April 15, 2026' },
      { key: 'valid_until', label: 'Valid Until', example: 'October 15, 2026' },
      { key: 'entries', label: 'Entries', example: 'Multiple' },
    ],
  },
  {
    id: 'tpl-6',
    name: 'Post-Trip Follow Up',
    category: 'Custom',
    icon: 'ri-star-smile-line',
    color: '#f43f5e',
    description: 'Send after return date. Thank customers and request a review.',
    usageCount: 41,
    lastUsed: '2026-03-20',
    status: 'Active',
    headerText: 'Welcome Back! \ud83c\udfe0',
    bodyText: 'Dear {{customer_name}},\n\nWelcome back from your trip to *{{destination}}*! We hope you had an absolutely amazing experience.\n\nYour feedback means the world to us. It takes just 2 minutes to leave a review and helps other travelers plan their dream trips.\n\n\u2b50 *Leave a Google Review*\n\nAlso, ask us about our *Loyalty Rewards* — your next booking earns you exclusive discounts!\n\nWarm regards,\n{{agent_name}}',
    footerText: 'Horizon Travel & Tourism',
    buttons: [
      { type: 'URL', label: 'Leave a Review', value: 'https://g.page/horizontravel/review' },
      { type: 'URL', label: 'Book Next Trip', value: 'https://horizontravel.ae' },
    ],
    variables: [
      { key: 'customer_name', label: 'Customer Name', example: 'David Chen' },
      { key: 'destination', label: 'Destination', example: 'Japan' },
      { key: 'agent_name', label: 'Agent Name', example: 'Mohammed Khalid' },
    ],
  },
];

export const waSentMessages: WaSentMessage[] = [];

export const waKPIs = {
  totalSent: 0,
  deliveryRate: 0,
  readRate: 0,
  responseRate: 0,
  activeTemplates: 6,
  monthlyLimit: 1000,
  monthlyUsed: 0,
};
